import { REACT_APP_API_HOST as API_HOST, REACT_APP_CDN_URL, REACT_APP_ESIGN_URL } from 'envs';
import { IInviteNavigateModalSteps } from 'global-stores';
import { IEnvironmentHost, localHost } from 'helpers';
import simpliciWordmark from 'assets/logo/simplici-wordmark.svg';
import simpliciMark from 'assets/logo/simplici-mark.svg';

interface IBrandColor {
	colorName: string;
	color: string;
	class: string;
}

interface IBrandFont {
	font: string;
	class: string;
}

export const REGEX_ACCEPT_ONLY_NUMBER = /^[0-9\b]+$/;

export const REGEX = {
	// eslint-disable-next-line no-useless-escape
	isValidAppStoreUrl : /^(https?:\/\/(?:www\.)?(apple\.com|itunes\.apple\.com|developer\.apple\.com)\/[^\s]*)$/,
	// eslint-disable-next-line no-useless-escape
	isValidPlayStoreUrl : /^https:\/\/play\.google\.com(\/store\/apps\/details\?id=[a-zA-Z0-9._\-]+(&.*)?)?\/?$/,
	// eslint-disable-next-line no-useless-escape
	isValidUrl: /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:[0-9]{1,5})?(\/[^\s]*)?$/,
	isValidKey: /^[^\s]+$/
}

/*
 * The brand mark.
 *
 * These used to point at
 * storage.googleapis.com/satschel-public-assets/images/logo/simplici-{wordmark,icon}.png
 * — a pair of files last modified in January 2023 carrying the OLD uppercase
 * "SiMPLICI" mark. Every satschel repo and every branch (master, dev, release,
 * test) still points at those same two urls, so the whole product was rendering
 * a retired logo; the bucket object was never refreshed when the brand changed.
 *
 * Now vendored as local SVGs taken from the current brand mark on simplici.io
 * (lowercase "simplici" in #08f with the check over the i). Local rather than
 * hot-linked: the source is a WordPress upload path, which is not somewhere the
 * product should have a runtime dependency on. SVG rather than PNG so the
 * sidebar stays crisp at every width, and `simplici-mark.svg` is the check glyph
 * lifted verbatim out of the wordmark so the collapsed and expanded states are
 * the same artwork.
 */
export const LOGO = {
	LIGHT: simpliciWordmark,
};

export const BRAND_LOGO = simpliciMark;

export const RESPONSIVE_LOGO =
	window.innerWidth < 768 ? BRAND_LOGO : LOGO.LIGHT;

export const BRAND_PRIMARY_COLOR: IBrandColor[] = [
	{ colorName: 'blue', color: '#0051cc', class: 'color-blue' },
	{ colorName: 'red', color: 'red', class: 'color-red' },
	{ colorName: 'black', color: 'black', class: 'color-black' },
	{ colorName: 'orange', color: 'orange', class: 'color-orange' },
];

export const BRAND_FONT_SIZE: IBrandFont[] = [
	{ font: 'small', class: 'font-small' },
	{ font: 'default', class: 'font-default' },
	{ font: 'large', class: 'font-large' },
];

export const IMAGE_GCP_URL = REACT_APP_CDN_URL + '/images/simplici-media';

export const SENTRY_URL =
	'https://2c28c59194382675e97633c6e48497a3@o4505663140724736.ingest.sentry.io/4505663595675648';

export const WEBSITE_HOST = {
	prod: 'https://app.simplici.io',
	pp: 'https://admin.pp.satschel.com',
	stage: 'https://admin.stage.satschel.com',
};
export const SocialLink = [
	{
		url: 'https://www.linkedin.com/company/simplici/',
		icon: 'ri-linkedin-fill',
	},
	{
		url: 'https://twitter.com/simplici2023',
		icon: 'ri-twitter-x-fill',
	},
];

export const ASSETS = {
	NOTSUPPORTED_SVG: 'Mask_Group_5.svg',
};

export const MESSAGE = {
	ERROR: 'Oops! Something went wrong.',
	ERROR_TRY: 'Oops! Something went wrong. Please try again later.',
};

export const getLogos: any = {
	default: 'Bank_Default.svg',
	'Navy Federal Credit Union': 'Navy_Federal_Credit_Union.svg',
	Fidelity: 'Fidelity.svg',
	Wealthfront: 'Wealthfront.svg',
	'TD Bank': 'TD.svg',
	'Citibank Online': 'Citi.svg',
	'Citizens Bank': 'Citizens.svg',
	Chase: 'Chase.svg',
	'Bank of America': 'Bank_of_America.svg',
	USAA: 'USAA.svg',
	'Regions Bank': 'Regions_Financial_Corporation.svg',
	'Charles Schwab': 'Charles_Schwab_Corporation.svg',
	'American Express - Personal Savings': 'American_Express.svg',
	'American Express': 'American_Express.svg',
	'Digital Federal Credit Union': 'DigitalFederal_Credit_Union.svg',
	'Wells Fargo': 'Wells_Fargo.svg',
	'Marcus by Goldman Sachs': 'Marcus_By_Goldman_Sachs.svg',
};

export const TOAST_MESSAGES = {
	ERROR: 'Oops! Something went wrong.',
};

export const NewAppLink = 'https://simplici.io';

export const FallBackErrorMessage = {
	HEADING: "We're sorry, but it seems like something went wrong",
	DESCRIPTION:
		'We apologize for any inconvenience this may cause and appreciate your patience.',
	DESCRIPTION_2: 'Our team is already on it and working to resolve the problem',
};

export const USER_LINEAR_FLOW_DISABLED = [
	'6312047e127944ae8e147bfe', //Austin Prod
	'654884bc547dc65a687659ca', //Sourabh PP
	'65268a6f9b3ed1a5914b6631', //Sourabh Stage
	'651bd5b3e67631df65ceb333', //Sourabh Prod
];

export const companySatschelURL = [
	// Without an entry here the panel replaces ITSELF — every route, including
	// /login — with the "website you are trying to access is not whitelisted"
	// screen, so there is no way to sign in and add the domain from inside the
	// product. It has to be a known first-party origin.
	'https://dev2.simplici.io',
	'https://admin.stage.satschel.com',
	'https://admin.pp.satschel.com',
	'https://app.simplici.io',
	'https://admin.test.satschel.com',
	'https://beta.simplici.io',
	// v2 production and UAT. Missing here, every account on them hit the
	// screen above, and a client whitelist entry cannot fix it for everyone.
	'https://app.v2.simplici.io',
	'https://uat.simplici.io',
];

export const ALLOWEDDATADOGLOGSURLS = [
	'https://app.simplici.io',
	'https://admin.pp.satschel.com',
	'https://admin.stage.satschel.com',
];

type IEnvironment = { [key in IEnvironmentHost]: string };

export const environmentHost: IEnvironment | any = {
	nonStage: 'stage', // this has been added temporarly
	stage: 'stage',
	preprod: 'preprod',
	prod: 'prod',
};

export const sandboxEnvHost: IEnvironment | any = {
	nonStage: 'stage', // this has been added temporarly
	stage: 'stage',
	preprod: 'stage',
	prod: 'preprod',
};

export const environmentUrl: IEnvironment | any = {
	// __simplici_esign_local_signing__: matches the prefix esign.api actually
	// puts on a locally-created envelope's prepareUrl. Without this key,
	// admin.panel's own `env` value (esign.api's ENVIRONMENT var, which this one
	// service leaves at 'stage' even in a local stack) sends a local envelope's
	// signing flow through the STAGE branch below — the stage host never
	// matches a localhost prepareUrl, the strip is a no-op, and the unstripped
	// absolute local URL gets concatenated onto stage's API host by the
	// embedded web component instead. See getEnvelopeEnv() in
	// views/simplici-sign-dashboard/components/document-start-now.tsx, which
	// is what actually decides 'local' vs trusting the server's `env` field —
	// same "detect localhost directly, don't trust the reported string"
	// approach _infra/patch-esign-local-signing.sh already uses for the
	// applicant-facing signAgreement flow, and for the same reason.
	//
	// NOT A LITERAL PORT. The local stack's esign.frontend port is whatever
	// `make up` resolved (`_infra/ports.env` ESIGN_FRONTEND_PORT, 3031 on this
	// box) and __ENV.js already carries it as REACT_APP_ESIGN_URL. A hard-coded
	// :3030 here silently missed a :3031 prepareUrl, the strip was a no-op, the
	// absolute URL went to <simplici-sign> as its envelope path, and the
	// prepare modal opened blank — the exact failure described above, just one
	// port over. Falls back to :3030 only when the env is absent.
	local: `${(REACT_APP_ESIGN_URL || 'http://localhost:3030').replace(/\/+$/, '')}/`,
	// dev2 puts all three frontends behind ONE host and serves esign under a
	// /esign base path, so the prefix to strip is origin + base path — not a
	// bare origin like every other entry.
	dev2: 'https://dev2.simplici.io/esign/',
	// uat: same one-host layout as dev2 (SIM-76).
	uat: 'https://uat.simplici.io/esign/',
	// v2 production, same one-host layout. Not `prod`: that key is v1
	// production (esign.simplici.io) and v2's esign.api reports the same string.
	v2prod: 'https://app.v2.simplici.io/esign/',
	nonStage: 'https://esign.stage.satschel.com/', // this is added temperorly
	stage: 'https://esign.stage.satschel.com/',
	preprod: 'https://esign.pp.satschel.com/',
	prod: 'https://esign.simplici.io/',
	pp: 'https://esign.pp.satschel.com/',
	beta: 'https://esign.beta.simplici.io/',
};

/**
 * WHICH esign.frontend BUILD AN EMBEDDED IFRAME SHOULD LOAD.
 *
 * Separate from `envHost` on purpose, and this is the whole reason it exists.
 *
 * `environmentHost()` decides from REACT_APP_API_HOST: it tests for stage, pp,
 * prod and beta, and otherwise DEFAULTS to 'stage'. dev2's API host is
 * api.dev2.simplici.io, which matches none of them — so every esign iframe on
 * dev2 resolved to `environmentUrl.stage` and loaded
 * esign.stage.satschel.com. `environmentUrl` has carried a `dev2` entry all
 * along; `envHost` simply cannot return it, and IEnvironmentHost does not list
 * it.
 *
 * The visible cost was the View and Correct dialogs rendering in light mode on
 * a dark panel, and no stylesheet in this repo could fix it: the theme reaches
 * an iframe as a `?theme=` param that the FRAME's own build has to read, and
 * the stage build has no dark rules at all (its main.css contains zero
 * `data-sv2-theme` selectors, against twenty in dev2's). The dark CSS was never
 * missing — it was in a build nothing was pointing at.
 *
 * The quieter cost is that dev2 was embedding another environment's frontend,
 * which then talks to that environment's API. That is the same class of mistake
 * `resolveEsignEmbed` below was written for; these two call sites just never
 * used it, because they mint a viewer URL rather than consuming a prepareUrl.
 *
 * DELIBERATELY NOT FIXED BY TEACHING `envHost` ABOUT dev2. That value also
 * decides the sidebar's tab list, whether the axios mock is installed and
 * whether the marketing scripts load; returning a new string from it would
 * change all three. This resolver is read only where an esign iframe src is
 * built, so nothing else can move.
 */
export const esignEmbedHost = (): string => {
	// The panel served from localhost embeds the esign app the local stack is
	// running, whatever port `make up` resolved into REACT_APP_ESIGN_URL.
	if (localHost) return environmentUrl.local;
	// dev2 puts all three frontends behind one host, esign under /esign.
	if (/dev2/i.test(API_HOST ?? '')) return environmentUrl.dev2;
	// uat: one host, esign under /esign, exactly like dev2 (SIM-76).
	if (/(^|[/.])uat\./i.test(API_HOST ?? '')) return environmentUrl.uat;
	// v2 production: api.v2.simplici.io, esign under app.v2.simplici.io/esign.
	if (/api\.v2\.simplici\.io/i.test(API_HOST ?? '')) return environmentUrl.v2prod;
	return '';
};

/**
 * Decide which environment an embedded e-sign surface belongs to from the
 * PREPARE URL, not from the `env` string esign.api reports alongside it.
 *
 * esign.api answers with its own `ENVIRONMENT` variable, and on every non-stage
 * deployment that value is still `stage` — dev2's esign.api reports `stage`
 * while handing back `https://dev2.simplici.io/esign/prepare/envelope/<id>`,
 * exactly as the local stack reports `stage` for a localhost prepareUrl.
 *
 * Trusting it fails twice over, and neither failure names itself:
 *
 *  1. `environmentUrl['stage']` never prefix-matches the URL, so the strip
 *     below is a no-op and the WHOLE ABSOLUTE URL is handed to
 *     <simplici-sign> as its envelope id. The widget then requests
 *     `.../envelopes/https://dev2.simplici.io/esign/<id>`.
 *  2. The widget resolves its API host from the same `env`, so that doubled
 *     request goes to api.stage.satschel.com — cross-origin from dev2, so the
 *     browser blocks it as CORS, esign's own router navigates to
 *     /server-error, and the host page shows an empty modal. Every dev2
 *     service log stays clean, because the request never reached dev2.
 *
 * So match on the URL shape, which is true by construction. Deliberately
 * narrow: anything that is not recognisably local or dev2 keeps the reported
 * `env`, leaving stage/pp/prod/beta untouched.
 */
const ESIGN_ENV_BY_URL_SHAPE: [RegExp, string][] = [
	[/^https?:\/\/(localhost|127\.0\.0\.1)[:/]/i, 'local'],
	[/^https?:\/\/dev2\.simplici\.io\//i, 'dev2'],
	// SIM-76: UAT's esign.api answers env "uat" with a uat.simplici.io/esign
	// prepareUrl. Unrecognised, the whole absolute URL went to the widget as its
	// envelope path and `uat` had no API host, so "Start now" spun forever.
	[/^https?:\/\/uat\.simplici\.io\//i, 'uat'],
	// v2 production reports env "prod", which already means v1 production
	// (esign.simplici.io / api.satschel.com). Its URL shape tells them apart.
	[/^https?:\/\/app\.v2\.simplici\.io\//i, 'v2prod'],
];

export interface IEsignEmbedConfig {
	prepareUrl: string;
	env: string;
}

/**
 * Resolve the `env` and the relative `signUrl` to hand <simplici-sign>.
 * Returns an empty `signUrl` for an empty prepareUrl, which is what the
 * callers already treat as "still loading".
 */
export const resolveEsignEmbed = ({
	prepareUrl,
	env,
}: IEsignEmbedConfig): { env: string; signUrl: string } => {
	const url = prepareUrl ?? '';
	const shape = ESIGN_ENV_BY_URL_SHAPE.find(([pattern]) => pattern.test(url));
	const resolvedEnv = shape ? shape[1] : env;
	const base = environmentUrl[resolvedEnv];
	return {
		env: resolvedEnv,
		signUrl: base ? url.replace(base, '') : url,
	};
};

export const isMobileView = window.innerWidth < 600;

/**
 * session out time is 30 mins
 * */
export const sessionOutTime = 1800000;
// export const sessionOutTime = 30000

export const INVITE_MODAL_STEPS: {
	[key in Uppercase<IInviteNavigateModalSteps>]: IInviteNavigateModalSteps;
} = {
	CREATE: 'create',
	CSV: 'csv',
	SIGNOPTIONS: 'signOptions',
	CONFIGCOMPLEX: 'configComplex',
	SIGNAGGREMENT: 'signAggrement',
	PROOFREADING: 'proofReading',
	CONNECTBANK: 'connectBank',
	CONFIGCAPTABLE: 'configCaptable',
	BILLING: 'billing',
	SELECTBANKFLOW: 'selectBankFLow',
	PERSONALFORM: 'personalForm',
	BUSINESSFORM: 'businessForm',
	ACCOUNTPROCESSING: 'accountProcessing',
	ESCROWSUCCESS: 'escrowSuccess',
	REMINDERNOTIFICATION: 'reminderNotification',
	SIGNACCESSCODE: 'signAccessCode',
};

export type IPacketStatus = 'completed' | 'configured' | 'voided';

export const PACKET_STATUS: {
	[key in Uppercase<IPacketStatus>]: Lowercase<key>;
} = {
	COMPLETED: 'completed',
	CONFIGURED: 'configured',
	VOIDED: 'voided',
};

export const createdAt = new Date(2021, 0, 1);

export const ROUTE_TAB_NAMES = ['esign-inbox', 'esign-sent'];

export const DocumentAccessMessage = {
	PROMPT_MESSAGE: 'Enter the password to access the document.',
	ERROR_MESSAGE: 'Invalid password. Access denied. Please try again.',
};

export const MODAL_TYPE = {
	LEAVE: 'leave',
	DELETE: 'delete',
};

export type ILoadingStatus = 'idle' | 'pending' | 'success';

export const LOADING_STATUS: {
	[key in Uppercase<ILoadingStatus>]: Lowercase<key>;
} = {
	IDLE: 'idle',
	PENDING: 'pending',
	SUCCESS: 'success',
};

export const PLAID_COUNTRY_CODE = ['US', 'CA'];

// Test phone numbers that bypass validation
export const TEST_PHONE_NUMBERS = ['0000000000', '0000000001', '0000000002'];
